import React from 'react';
import { useNavigate } from 'react-router-dom';

// Deterministic gradient palette for presence avatars (cycled by index).
const USER_COLORS = [
  '#22d3ee, #2563eb',
  '#f472b6, #9333ea',
  '#fbbf24, #ea580c',
  '#fb923c, #ef4444',
  '#10b981, #0d9488',
  '#a855f7, #6366f1',
];

// Per-icon colour palette so the rail feels alive instead of all-gray. The
// `active` state gets a tinted background + a brighter ink; inactive state
// keeps the icon coloured (but muted to slate hover-bg) so the rail still
// reads as a single column rather than a rainbow at rest.
const NAV_COLORS = {
  notes:    { active: 'bg-blue-50 dark:bg-blue-500/[0.10] text-blue-600 dark:text-blue-300',         idle: 'text-blue-500 dark:text-blue-400' },
  audio:    { active: 'bg-violet-50 dark:bg-violet-500/[0.10] text-violet-600 dark:text-violet-300',  idle: 'text-violet-500 dark:text-violet-400' },
  profile:  { active: 'bg-pink-50 dark:bg-pink-500/[0.10] text-pink-600 dark:text-pink-300',         idle: 'text-pink-500 dark:text-pink-400' },
  schedule: { active: 'bg-emerald-50 dark:bg-emerald-500/[0.10] text-emerald-600 dark:text-emerald-300', idle: 'text-emerald-500 dark:text-emerald-400' },
  prior:    { active: 'bg-indigo-50 dark:bg-indigo-500/[0.10] text-indigo-600 dark:text-indigo-300', idle: 'text-indigo-500 dark:text-indigo-400' },
  reports:  { active: 'bg-amber-50 dark:bg-amber-500/[0.10] text-amber-600 dark:text-amber-300',     idle: 'text-amber-500 dark:text-amber-400' },
  settings: { active: 'bg-slate-100 dark:bg-slate-700/40 text-slate-600 dark:text-slate-300',        idle: 'text-slate-500 dark:text-slate-400' },
};

const NavBtn = ({ label, color = 'settings', active = false, disabled = false, disabledTitle, onClick, children }) => {
  const c = NAV_COLORS[color] || NAV_COLORS.settings;
  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled}
      aria-label={label}
      title={disabled ? (disabledTitle || `${label} unavailable`) : label}
      onClick={disabled ? undefined : onClick}
      data-active={active ? 'true' : 'false'}
      className={[
        'w-9 h-9 rounded-lg flex items-center justify-center transition-colors',
        disabled
          ? 'opacity-45 cursor-not-allowed text-slate-400 dark:text-slate-500'
          : active
            ? c.active
            : `${c.idle} hover:bg-slate-100 dark:hover:bg-slate-800/40`,
      ].join(' ')}
    >
      {children}
    </button>
  );
};

// Simple inline SVG icons matching the mockup's stroke style
const I = ({ d }) => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

// Sun icon path (shown when dark mode is active — clicking switches to light)
const SUN_PATH = "M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z";
// Moon icon path (shown when light mode is active — clicking switches to dark)
const MOON_PATH = "M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z";

export default function IconRail({
  theme = 'light',
  onToggleTheme,
  currentPatientUuid,
  scheduledDate = null, // YYYY-MM-DD of the active visit; used to deep-link the Schedule icon
  onOpenReports,
  onOpenPriorVisits, // () => void — opens the PriorVisitsModal (list + inline preview of earlier visits)
  userInitials = '?',
  presenceUsers = null, // [{ id, name?, initials, color?, cursorIndex? }] — users currently viewing the same doc
  onAvatarClick, // (user) => void — called when a presence avatar is clicked (scroll-to-cursor)
}) {
  const navigate = useNavigate();
  // Per spec:
  //   File (active) — current clinic document page
  //   Profile — /visit/<uuid> (the patient's full PatientPage)
  //   Schedule (calendar) — /schedules
  //   Reports — opens view-reports modal
  //   Settings — /settings (shared SettingsPage)
  return (
    <aside className="w-14 shrink-0 flex flex-col items-center justify-between py-3 border-r border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f]">
      <div className="flex flex-col items-center gap-1">
        {/* Collaborative presence: one avatar per user currently viewing this document.
            Falls back to the current user's avatar when nobody else is editing. */}
        {presenceUsers && presenceUsers.length > 0 ? (
          <>
            {presenceUsers.slice(0, 5).map((u, i) => {
              const interactive = !!onAvatarClick;
              const hasCursor = u.cursorIndex != null;
              const tooltip = hasCursor
                ? `${u.name || u.initials} — click to jump to their cursor`
                : `${u.name || u.initials} — hasn't moved their cursor yet`;
              const handle = () => onAvatarClick?.(u);
              return (
                <button
                  type="button"
                  key={u.userId || u.id || i}
                  onClick={handle}
                  disabled={!interactive}
                  title={tooltip}
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-indigo-500/30 transition-opacity disabled:cursor-default"
                  style={{
                    background: u.color || `linear-gradient(135deg, ${USER_COLORS[i % USER_COLORS.length]})`,
                    opacity: u.disconnected_at ? 0.6 : 1,
                  }}
                >
                  {(u.initials || (u.name || '?').slice(0, 2)).toUpperCase()}
                </button>
              );
            })}
            {presenceUsers.length > 5 && (
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-100 font-bold text-xs"
                title={presenceUsers.slice(5).map((u) => u.name || u.initials).join('\n')}
              >
                +{presenceUsers.length - 5}
              </div>
            )}
          </>
        ) : (
          <div
            className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-indigo-500/30"
            title="You — only one viewer"
          >
            {userInitials.slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="h-px w-6 bg-slate-200 dark:bg-slate-800/60 my-2" />
        {/* File icon removed 2026-06-28 (Leejoe): it had no onClick — was
            just a visual "you're on the chart page" indicator. The URL +
            patient header already convey that. KISS. The chart-page
            "active" state is implicit when the rail is rendered (we only
            mount this rail on the clinic chart). Maximize/collapse for
            the note + reports panels lives on the panels themselves, not
            here. */}
        <NavBtn
          label="Patient profile"
          color="profile"
          disabled={!currentPatientUuid}
          disabledTitle="Patient profile — no patient loaded yet"
          onClick={() => {
            if (!currentPatientUuid) return;
            // Preserve the active visit date so PatientPage lands on the
            // right day instead of "today" — matches how Schedule deep-links.
            const qs = scheduledDate ? `?date=${scheduledDate}` : '';
            navigate(`/visit/${currentPatientUuid}${qs}`);
          }}
        >
          <I d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </NavBtn>
        <NavBtn
          label="Schedule"
          color="schedule"
          onClick={() => navigate(scheduledDate ? `/schedules?date=${scheduledDate}` : '/schedules')}
        >
          <I d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </NavBtn>
        {/* Prior visits — opens a popup listing every earlier visit for this
            patient, with a built-in read-only preview so the clinician can
            glance at last time's note without leaving the active chart. */}
        <NavBtn
          label="Prior visits"
          color="prior"
          disabled={!currentPatientUuid || !onOpenPriorVisits}
          disabledTitle={
            !currentPatientUuid
              ? 'Prior visits — no patient loaded yet'
              : 'Prior visits unavailable'
          }
          onClick={onOpenPriorVisits}
        >
          {/* Clock-with-arrow icon — distinct from the Schedule calendar so
              the two don't read as the same control. */}
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </NavBtn>
        {/* Removed 2026-06-28 (Leejoe KISS pass):
              - Reports icon: redundant with the Reports tab in the right
                side-panel + the per-source Process buttons in note. KISS.
              - Settings icon: not needed in chart context. Settings lives
                on the main top blue bar (Schedule page navbar). No reason
                to jump to Settings mid-clinic day.
            Both remain reachable from the schedule page navbar. */}
      </div>
      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          aria-label="Toggle theme"
          title="Toggle theme"
          onClick={onToggleTheme}
          className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/40"
        >
          <I d={theme === 'dark' ? SUN_PATH : MOON_PATH} />
        </button>
        <div
          className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shadow-lg shadow-blue-500/30"
          title="Your account"
        >
          {userInitials.slice(0, 2).toUpperCase()}
        </div>
      </div>
    </aside>
  );
}
